import React, {useState, useEffect} from 'react';
import {Formik} from "formik";
import {Button, Card, CardBody, Col, CustomInput, FormGroup, Input, Label, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import * as Yup from "yup";
import CardHeader from "reactstrap/es/CardHeader";
import api from "../services/api";
import {receiveStudent} from "../redux/auth/actions";
import {useHistory} from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import NumberFormat from 'react-number-format';

const ProfileEdit = () => {
    const user = useSelector(state => state.auth.user);
    const [lookups, setLookups] = useState(null);
    const dispatch = useDispatch();
    const history = useHistory();

    const {customFields} = user.student;

    const initialValues = {
        gender: user.student.gender,
        birthdate: moment(user.student.birthdate).toDate(),
        phone_number: user.student.phone_number,
        address_line_1: user.student.address_line_1 ?? "",
        address_line_2: user.student.address_line_2 ?? "",
        city: user.student.city,
        state: user.student.state,
        zip_code: user.student.zip_code,
        custom_fields: customFields.reduce((c, f) => {
            c[f.lookup.id] = f.value;

            return c;
        }, {})
    };

    const ValidationSchema = Yup.object().shape({
        gender: Yup.string().required().oneOf(["male", "female", "other"]),
        birthdate: Yup.date().required(),
        phone_number: Yup.string().required(),
        address_line_1: Yup.string().required(),
        city: Yup.string().required(),
        state: Yup.string().required(),
        zip_code: Yup.string().required()
    });

    useEffect(() => {
        (async () => {
            const lookups = await api.fetchLookups(['studentCustomField']).then(r => r.data.data);
            lookups.studentCustomField.map(field => {
                if (initialValues.custom_fields[field.value] === undefined) {
                    initialValues.custom_fields[field.value] = "";
                }
            });

            setLookups(lookups);
        })();
    }, []);

    const onSubmit = (values, setSubmitting) => {
        api.editProfile({...values, birthdate: moment(values.birthdate).format('MM/DD/YYYY')}).then(response => {
            dispatch(receiveStudent(response.data.data));
            history.push('/profile');
        }).catch(() => {
            setSubmitting(false);
        })
    };

    return (
        <div className="page">
            <h1 className="page-header">Edit Profile</h1>
            {lookups &&
            <Formik
                initialValues={initialValues}
                validationSchema={ValidationSchema}
                onSubmit={(values, {setSubmitting}) => onSubmit(values, setSubmitting)}
            >
                {props => {
                    const {
                        values, errors, touched, handleChange, handleBlur,
                        handleSubmit, isSubmitting, setFieldValue,
                        setFieldTouched
                    } = props;
                    return (
                    <form onSubmit={handleSubmit}>
                        <Card className="mb-3">
                            <CardHeader>Personal Details</CardHeader>
                            <CardBody>
                                <FormGroup>
                                    <Label for="gender-male">Gender</Label>
                                    <div>
                                        <CustomInput type="radio" id="gender-male" label="Male" value="male"
                                                     name="gender"
                                                     checked={values.gender === 'male'}
                                                     onChange={handleChange}
                                                     onBlur={handleBlur}
                                                     inline
                                        />
                                        <CustomInput type="radio" id="gender-female" label="Female" value="female"
                                                     name="gender"
                                                     checked={values.gender === 'female'}
                                                     onChange={handleChange}
                                                     onBlur={handleBlur}
                                                     inline
                                        />
                                        <CustomInput type="radio" id="gender-other" label="Other" value="other"
                                                     name="gender"
                                                     checked={values.gender === 'other'}
                                                     onChange={handleChange}
                                                     onBlur={handleBlur}
                                                     inline
                                        />
                                    </div>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="birthdate">Birthdate</Label>
                                    <DatePicker
                                        selected={values.birthdate}
                                        onChange={date => setFieldValue('birthdate', date)}
                                        dateFormat="MM/dd/yyyy"
                                        customInput={
                                            <Input type="text" id="birthdate"
                                                   invalid={!!(errors.birthdate && touched.birthdate)}
                                                   className="elementor-field"
                                            />
                                        }
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="phone-number">Phone Number</Label>
                                    <NumberFormat
                                        value={values.phone_number}
                                        customInput={Input}
                                        allowEmptyFormatting
                                        mask="#"
                                        format="(###) ###-####"
                                        onValueChange={values => {
                                            setFieldValue('phone_number', values.formattedValue);
                                        }}
                                        invalid={!!(errors.phone_number && touched.phone_number)}
                                        onBlur={() => {
                                            setFieldTouched('phone_number')
                                        }}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>

                        <Card className="mb-3">
                            <CardHeader>Address</CardHeader>
                            <CardBody>
                                <FormGroup>
                                    <Label for="address-line-1">Address Line 1</Label>
                                    <Input type="textarea" name="address_line_1" id="address-line-1"
                                           onChange={handleChange}
                                           onBlur={handleBlur}
                                           value={values.address_line_1}
                                           invalid={!!(errors.address_line_1 && touched.address_line_1 && errors.address_line_1)}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="address-line-2">Address Line 2</Label>
                                    <Input type="textarea" name="address_line_2" id="address-line-2"
                                           onChange={handleChange}
                                           onBlur={handleBlur}
                                           value={values.address_line_2}
                                           invalid={!!(errors.address_line_2 && touched.address_line_2 && errors.address_line_2)}
                                    />
                                </FormGroup>

                                <Row>
                                    <Col>
                                        <FormGroup>
                                            <Label for="city">City</Label>
                                            <Input type="text" name="city" id="city"
                                                   onChange={handleChange}
                                                   onBlur={handleBlur}
                                                   value={values.city}
                                                   invalid={!!(errors.city && touched.city && errors.city)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup>
                                            <Label for="state">State</Label>
                                            <Input type="text" name="state" id="state"
                                                   onChange={handleChange}
                                                   onBlur={handleBlur}
                                                   value={values.state}
                                                   invalid={!!(errors.state && touched.state && errors.state)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup>
                                            <Label for="zip-code">Zip Code</Label>
                                            <Input type="text" name="zip_code" id="zip_code"
                                                   onChange={handleChange}
                                                   onBlur={handleBlur}
                                                   value={values.zip_code}
                                                   invalid={!!(errors.zip_code && touched.zip_code && errors.zip_code)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {!!lookups.studentCustomField.length &&
                        <div className="card mb-3">
                            <div className="card-body mb-n3">
                                {lookups.studentCustomField.map((customField, index) => {
                                    return (
                                        <div className="form-group" key={index}>
                                            <label htmlFor={`custom-field-${customField.id}`}>
                                                {customField.label}
                                            </label>
                                            <input type="text" id={`custom-field-${customField.value}`}
                                                   className="form-control"
                                                   value={values.custom_fields[customField.value]}
                                                   onChange={e => {
                                                       values.custom_fields[customField.value] = e.target.value;

                                                       setFieldValue("custom_fields", values.custom_fields);
                                                   }}
                                                   onBlur={handleBlur}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>}

                        <Button type="submit" disabled={isSubmitting}>
                            Submit
                        </Button>
                    </form>
                    );
                }}
            </Formik>}
        </div>
    );
};

export default ProfileEdit;
